import React from 'react';
import { usePluginRoutes } from './usePluginRoutes';

/**
 * Debug component to help troubleshoot route registration issues
 */
export const RouteDebugger: React.FC = () => {
  const { routes, getRoutesByPlugin, getNamespaces } = usePluginRoutes();

  const satraktRoutes = getRoutesByPlugin('Sattrakt');
  const namespaces = getNamespaces;

  return (
    <div style={{ 
      padding: '20px', 
      border: '1px solid #ccc', 
      margin: '10px', 
      fontFamily: 'monospace',
      backgroundColor: '#f5f5f5'
    }}>
      <h3>🐛 Route Registration Debug</h3>
      
      <div style={{ marginBottom: '15px' }}>
        <strong>Total Registered Routes:</strong> {routes.length}
      </div>

      <div style={{ marginBottom: '15px' }}>
        <strong>Available Namespaces:</strong> {namespaces.join(', ') || 'none'}
      </div>

      <div style={{ marginBottom: '15px' }}>
        <strong>Sattrakt Plugin Routes:</strong> {satraktRoutes.length}
        {satraktRoutes.length > 0 && (
          <div style={{ marginLeft: '20px', marginTop: '5px' }}>
            {satraktRoutes.map((route, index) => (
              <div key={index} style={{ marginBottom: '5px' }}>
                • {route.path} - {route.title} (ID: {route.id})
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ marginBottom: '15px' }}>
        <strong>All Routes:</strong>
        {routes.length === 0 ? (
          <div style={{ color: 'red', marginLeft: '20px' }}>
            ❌ No routes registered! This indicates the plugin registration might not be working.
          </div>
        ) : (
          <div style={{ marginLeft: '20px', marginTop: '5px' }}>
            {routes.map((route, index) => (
              <div key={index} style={{ marginBottom: '5px' }}>
                • <strong>{route.pluginName}</strong>: {route.path} - {route.title || 'No title'}
                <br />
                  <span style={{ color: '#666', fontSize: '12px' }}>
                    ID: {route.id}, Namespace: {route.namespace || 'default'}, 
                    Registered: {route.registeredAt.toLocaleTimeString()}
                  </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ marginBottom: '15px' }}>
        <strong>Registration Test:</strong>
        <div style={{ marginLeft: '20px', marginTop: '5px', color: '#666' }}>
          Expected route: /dashboard from Sattrakt plugin
          <br />
          {satraktRoutes.find(r => r.path === '/dashboard') ? (
            <span style={{ color: 'green' }}>✅ Found!</span>
          ) : (
            <span style={{ color: 'red' }}>❌ Not found</span>
          )}
        </div>
      </div>

      <details style={{ marginBottom: '15px' }}>
        <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
          📊 Raw Route Data (Click to expand)
        </summary>
        <pre style={{ 
          backgroundColor: '#fff', 
          padding: '10px', 
          overflow: 'auto',
          fontSize: '11px'
        }}>
          {JSON.stringify(routes, null, 2)}
        </pre>
      </details>

      <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#e8f4f8', border: '1px solid #bee5eb' }}>
        <strong>💡 Troubleshooting Tips:</strong>
        <ul style={{ marginTop: '5px' }}>
          <li>Make sure your plugin is registered with the PluginRegistryProvider</li>
          <li>Check the browser console for route registration logs</li>
          <li>Verify the plugin object structure matches the Plugin interface</li>
          <li>Ensure the Requests component is properly imported</li>
        </ul>
      </div>
    </div>
  );
};

/**
 * Hook to get debug information about route registration
 */
export const useRouteDebug = () => {
  const { routes, getRoutesByPlugin } = usePluginRoutes();

  const debugInfo = {
    totalRoutes: routes.length,
    routesByPlugin: routes.reduce((acc, route) => {
      acc[route.pluginName] = (acc[route.pluginName] || 0) + 1;
      return acc;
    }, {} as Record<string, number>),
    allRoutes: routes.map(route => ({
      plugin: route.pluginName,
      path: route.path,
      title: route.title,
      id: route.id,
      registeredAt: route.registeredAt
    }))
  };

  const logDebugInfo = () => {
    console.group('🐛 Route Registration Debug');
    console.log('Total routes:', debugInfo.totalRoutes);
    console.log('Routes by plugin:', debugInfo.routesByPlugin);
    console.table(debugInfo.allRoutes);
    console.groupEnd();
  };

  return {
    debugInfo,
    logDebugInfo,
    getSatraktRoutes: () => getRoutesByPlugin('Sattrakt'),
    hasRoute: (path: string) => routes.some(r => r.path === path)
  };
};